import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { money } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyMarkPaidForm, toMarkPaidBody } from '../api/mappers';
import { useMarkOrderPaid } from '../hooks/use-order-mutations';
import {
  markPaidSchema,
  type MarkPaidFormInput,
  type MarkPaidFormOutput,
} from '../schemas/mark-paid-schema';
import { orderConflictKey, outstanding } from '../schemas/order-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['amount', 'reference', 'paidAt', 'note']);

/** `body.amount.currency` and `body.amount` both belong to the one amount field. */
const mapField = (field: string) => (field.startsWith('amount') ? 'amount' : field);

export interface MarkPaidDialogProps {
  storeId: string;
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Records a manual payment (D-031). The amount defaults to the outstanding balance and is only
 * editable once "partial" is on; the backend rejects anything above the balance with 422 on
 * `body.amount` and answers 409 once the order is no longer awaiting payment. Dates are the
 * store's calendar days (ADR 43).
 */
export function MarkPaidDialog({ storeId, order, open, onOpenChange }: MarkPaidDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const markPaid = useMarkOrderPaid(storeId);
  const totalAmount = order.total.amount;
  const paidAmount = order.paid.amount;
  const { currency } = order;
  const balance = useMemo(
    () =>
      outstanding({
        total: money(totalAmount, currency),
        paid: money(paidAmount, currency),
        currency,
      }),
    [totalAmount, paidAmount, currency],
  );
  const today = format.dateKey(new Date());
  const schema = useMemo(() => markPaidSchema(balance, today), [balance, today]);
  const form = useForm<MarkPaidFormInput, unknown, MarkPaidFormOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: emptyMarkPaidForm(balance, today),
  });
  const partial = form.watch('partial');
  const instructions =
    order.payments.find((p) => p.methodSnapshot?.instructions)?.methodSnapshot?.instructions ??
    order.paymentInstructions;

  const close = () => {
    onOpenChange(false);
    markPaid.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await markPaid.mutateAsync({
        id: order.id,
        body: toMarkPaidBody(values, format.timeZone),
      });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      const conflict = orderConflictKey(error, 'orders:markPaid.errors.notPayable');
      form.setError('root.server', {
        type: 'server',
        message: conflict ? t(conflict) : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('orders:markPaid.title', { name: order.name })}</DialogTitle>
          <DialogDescription>{t('orders:markPaid.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <p className="text-sm font-medium tabular-nums" data-testid="outstanding">
              {t('orders:markPaid.outstanding', { amount: format.money(balance) })}
            </p>
            <FormField
              control={form.control}
              name="partial"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>{t('orders:markPaid.partial')}</FormLabel>
                    <FormDescription>{t('orders:markPaid.partialHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (!checked) {
                          form.setValue('amount', balance);
                          form.clearErrors('amount');
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {partial ? (
              <FormField
                control={form.control}
                name="amount"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('orders:markPaid.amount')}</FormLabel>
                    <FormControl>
                      <MoneyInput
                        currency={order.currency}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <p className="text-muted-foreground text-sm">
                {t('orders:markPaid.fullAmount', { amount: format.money(balance) })}
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="reference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('orders:markPaid.reference')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        placeholder={t('orders:markPaid.referencePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="paidAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('orders:markPaid.paidAt')}</FormLabel>
                    <FormControl>
                      <DatePicker max={today} timeZone={format.timeZone} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:markPaid.note')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder={t('orders:markPaid.notePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {instructions ? (
              <div className="bg-muted/50 rounded-md border p-3 text-sm">
                <p className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                  {t('orders:markPaid.instructions')}
                </p>
                <p className="whitespace-pre-line">{instructions}</p>
              </div>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={markPaid.isPending}>
                {markPaid.isPending ? t('orders:markPaid.submitting') : t('orders:markPaid.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
